import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

interface TableState {
  tables: Record<string, {
    currentPage: number
    pageSize: number
    sortBy: string
    sortOrder: 'asc' | 'desc'
    selectedRows: string[]
  }>

  setPage: (tableId: string, page: number) => void
  setPageSize: (tableId: string, size: number) => void
  setSort: (tableId: string, sortBy: string, sortOrder: 'asc' | 'desc') => void
  toggleSort: (tableId: string, sortBy: string) => void
  setSelectedRows: (tableId: string, rows: string[]) => void
  toggleRowSelection: (tableId: string, rowId: string) => void
  clearSelection: (tableId: string) => void
  getTableState: (tableId: string) => TableState['tables'][string]
}

const defaultTableState = {
  currentPage: 0,
  pageSize: 50,
  sortBy: '',
  sortOrder: 'asc' as const,
  selectedRows: [],
}

export const useTableStateStore = create<TableState>()(
  devtools(
    (set, get) => ({
      tables: {},

      setPage: (tableId, currentPage) =>
        set((state) => ({
          tables: {
            ...state.tables,
            [tableId]: { ...(state.tables[tableId] || defaultTableState), currentPage }
          }
        })),

      setPageSize: (tableId, pageSize) =>
        set((state) => ({
          tables: {
            ...state.tables,
            [tableId]: { ...(state.tables[tableId] || defaultTableState), pageSize, currentPage: 0 }
          }
        })),

      setSort: (tableId, sortBy, sortOrder) =>
        set((state) => ({
          tables: {
            ...state.tables,
            [tableId]: { ...(state.tables[tableId] || defaultTableState), sortBy, sortOrder }
          }
        })),

      toggleSort: (tableId, sortBy) =>
        set((state) => {
          const table = state.tables[tableId] || defaultTableState
          const sortOrder = table.sortBy === sortBy && table.sortOrder === 'asc' ? 'desc' : 'asc'
          return {
            tables: {
              ...state.tables,
              [tableId]: { ...table, sortBy, sortOrder }
            }
          }
        }),

      setSelectedRows: (tableId, selectedRows) =>
        set((state) => ({
          tables: {
            ...state.tables,
            [tableId]: { ...(state.tables[tableId] || defaultTableState), selectedRows }
          }
        })),

      toggleRowSelection: (tableId, rowId) =>
        set((state) => {
          const table = state.tables[tableId] || defaultTableState
          const selectedRows = table.selectedRows.includes(rowId)
            ? table.selectedRows.filter(id => id !== rowId)
            : [...table.selectedRows, rowId]
          return {
            tables: {
              ...state.tables,
              [tableId]: { ...table, selectedRows }
            }
          }
        }),

      clearSelection: (tableId) =>
        set((state) => ({
          tables: {
            ...state.tables,
            [tableId]: { ...(state.tables[tableId] || defaultTableState), selectedRows: [] }
          }
        })),

      getTableState: (tableId) =>
        get().tables[tableId] || defaultTableState,
    }),
    { name: 'TableStateStore' }
  )
)
